import React, { useState, useMemo, useEffect, useRef } from "react";
import { 
  X, 
  Disc, 
  Search, 
  Check, 
  AlertCircle, 
  Plus, 
  Minus, 
  MapPin, 
  Sparkles, 
  Settings2, 
  Boxes, 
  Gem, 
  Flame, 
  RefreshCw 
} from "lucide-react";
import { useCatalogContext } from "../../contexts/CatalogContext";
import { produtoDadosService } from "../../services/produtoDadosService";
import { getBaseSku, cleanSku, resolveModeloJoia, MODELOS_CANONICOS } from "../../utils/skuUtils";

export default function NovoMoldeModal({
  isOpen,
  onClose,
  onSuccess,
  allMoldes = [],
  user
}) {
  const { catalog = {}, findItem, moldeSkuMap = {} } = useCatalogContext();

  const [inputSku, setInputSku] = useState("");
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [tipoModelo, setTipoModelo] = useState("Pingente");
  const [gaveta, setGaveta] = useState("");
  const [ceraBruta, setCeraBruta] = useState("0");
  const [ceraPrecravada, setCeraPrecravada] = useState("0");
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);

  const searchInputRef = useRef(null);
  const dropdownRef = useRef(null);

  // Foco no input ao abrir
  useEffect(() => {
    if (isOpen) {
      setInputSku("");
      setSelectedProduct(null);
      setTipoModelo("Pingente");
      setGaveta("");
      setCeraBruta("0");
      setCeraPrecravada("0");
      setErrorMsg("");
      setShowSuggestions(false);
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    }
  }, [isOpen]);

  // Fecha sugestões ao clicar fora
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target) &&
          searchInputRef.current && !searchInputRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Normaliza o SKU digitado
  const cleanPai = useMemo(() => {
    if (!inputSku.trim()) return "";
    return getBaseSku(cleanSku(inputSku.trim())).toUpperCase();
  }, [inputSku]);

  // Verifica se o SKU já possui molde cadastrado
  const existingMolde = useMemo(() => {
    if (!cleanPai) return null;
    // Checa na lista de moldes carregados
    const foundInList = (allMoldes || []).find(m => {
      const p = getBaseSku(cleanSku(m.skuPai || "")).toUpperCase();
      return p === cleanPai;
    });
    if (foundInList) return foundInList;

    // Checa no mapa do catálogo
    if (moldeSkuMap[cleanPai]) {
      return { skuPai: cleanPai, temMolde: true };
    }
    return null;
  }, [cleanPai, allMoldes, moldeSkuMap]);

  // Sugestões do catálogo conforme digita
  const suggestions = useMemo(() => {
    const term = inputSku.trim().toLowerCase();
    if (!term || term.length < 2) return [];

    const results = [];
    const seenPai = new Set();

    const catalogEntries = Object.values(catalog || {});
    for (const item of catalogEntries) {
      if (!item || !item.sku) continue;
      const skuStr = String(item.sku).toLowerCase();
      const nameStr = String(item.name || item.full_data?.name || "").toLowerCase();
      const pai = getBaseSku(cleanSku(item.sku)).toUpperCase();

      if (skuStr.includes(term) || nameStr.includes(term)) {
        if (!seenPai.has(pai)) {
          seenPai.add(pai);
          results.push({
            ...item,
            skuPai: pai
          });
        }
      }
      if (results.length >= 8) break;
    }

    return results;
  }, [inputSku, catalog]);

  // Ao selecionar uma sugestão do catálogo
  const handleSelectProduct = (prod) => {
    const pai = prod.skuPai || getBaseSku(cleanSku(prod.sku)).toUpperCase();
    setInputSku(pai);
    setSelectedProduct(prod);
    setShowSuggestions(false);

    // Auto-detecta o tipo / modelo
    const resolved = resolveModeloJoia(pai, prod, null);
    if (resolved && resolved !== "Outros") {
      setTipoModelo(resolved);
    }
  };

  // Ao mudar o input livremente
  const handleInputChange = (e) => {
    const val = e.target.value.toUpperCase();
    setInputSku(val);
    setShowSuggestions(true);

    const pai = getBaseSku(cleanSku(val));
    if (pai) {
      const catItem = findItem ? (findItem(val) || findItem(pai)) : (catalog[val] || catalog[pai]);
      setSelectedProduct(catItem || null);
      const resolved = resolveModeloJoia(pai, catItem, null);
      if (resolved && resolved !== "Outros") {
        setTipoModelo(resolved);
      }
    } else {
      setSelectedProduct(null);
    }
  };

  // Submissão do cadastro
  const handleSave = async (openConfigImmediately = false) => {
    if (!cleanPai) {
      setErrorMsg("Informe um SKU válido para o novo molde.");
      return;
    }

    setSaving(true);
    setErrorMsg("");

    try {
      const userName = user?.name || "Operador";

      // 1. Habilita o molde no produto_dados e catalog
      await produtoDadosService.updateTemMolde(cleanPai, true);

      // 2. Define gaveta e saldos iniciais de cera (se preenchidos)
      const brutaNum = Math.max(0, parseInt(ceraBruta, 10) || 0);
      const precravadaNum = Math.max(0, parseInt(ceraPrecravada, 10) || 0);
      const gavetaLimpa = gaveta.trim().toUpperCase();

      if (gavetaLimpa || brutaNum > 0 || precravadaNum > 0) {
        await produtoDadosService.updateSubestoqueCera(cleanPai, {
          newBruta: brutaNum,
          newPrecravada: precravadaNum,
          localizacao_gaveta: gavetaLimpa,
          tipoOperacao: "CADASTRO_MOLDE",
          motivo: "Cadastro de novo molde de borracha na fábrica",
          usuario: userName
        });
      }

      // 3. Atualiza o tipo / modelo na configuração da borracha
      if (tipoModelo) {
        await produtoDadosService.updateConfigBorracha(cleanPai, {
          tipo_modelo: tipoModelo
        }, userName);
      }

      if (onSuccess) {
        onSuccess({
          skuPai: cleanPai,
          openConfigImmediately
        });
      }

      onClose();
    } catch (err) {
      console.error("Erro ao cadastrar novo molde:", err);
      setErrorMsg(err?.message || "Não foi possível habilitar o molde para este SKU.");
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/70 backdrop-blur-xs flex items-center justify-center p-3 sm:p-4 overflow-y-auto">
      <div 
        className="bg-white border border-slate-200 rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden flex flex-col max-h-[92vh] animate-in fade-in zoom-in-95 duration-150"
        onClick={(e) => e.stopPropagation()}
      >
        {/* CABEÇALHO */}
        <div className="bg-gradient-to-r from-slate-900 via-slate-800 to-amber-950 p-4 text-white flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-amber-500/20 border border-amber-400/30 text-amber-400 rounded-xl shadow-inner">
              <Disc size={22} className="animate-spin-slow" />
            </div>
            <div>
              <h2 className="text-base font-black uppercase tracking-wider text-white flex items-center gap-1.5">
                <span>Novo Molde / Borracha</span>
                <span className="text-[10px] bg-amber-500/30 text-amber-300 font-mono px-1.5 py-0.5 rounded border border-amber-400/30">
                  FÁBRICA
                </span>
              </h2>
              <p className="text-[11px] text-slate-300 mt-0.5">
                Vincule um SKU à injeção de borracha e defina o subestoque inicial
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="p-1.5 text-slate-400 hover:text-white rounded-lg hover:bg-white/10 transition cursor-pointer"
            title="Fechar"
          >
            <X size={20} />
          </button>
        </div>

        {/* CORPO DO FORMULÁRIO */}
        <div className="p-5 space-y-4 overflow-y-auto custom-scrollbar flex-1">
          {errorMsg && (
            <div className="bg-rose-50 border border-rose-200 text-rose-800 px-3.5 py-2.5 rounded-xl text-xs font-semibold flex items-center gap-2">
              <AlertCircle size={16} className="text-rose-600 shrink-0" />
              <span>{errorMsg}</span>
            </div>
          )}

          {/* CAMPO SKU COM BUSCA E AUTOCOMPLETE */}
          <div className="space-y-1.5 relative">
            <label className="text-xs font-black uppercase tracking-wider text-slate-800 flex items-center justify-between">
              <span>SKU do Produto / Peça</span>
              <span className="text-[10px] text-slate-400 font-normal">Digite ou busque no catálogo</span>
            </label>

            <div className="relative">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                ref={searchInputRef}
                type="text"
                value={inputSku}
                onChange={handleInputChange}
                onFocus={() => { if (suggestions.length > 0) setShowSuggestions(true); }}
                placeholder="Ex: MEDPIN, ANEL-01, PIN-COR..."
                className="w-full pl-9 pr-3 py-2 bg-white border-2 border-amber-400/80 focus:border-amber-600 rounded-xl text-sm font-mono font-black text-slate-900 focus:outline-none focus:ring-2 focus:ring-amber-400/40 uppercase shadow-inner"
              />
            </div>

            {/* DROPDOWN DE SUGESTÕES DO CATÁLOGO */}
            {showSuggestions && suggestions.length > 0 && (
              <div 
                ref={dropdownRef}
                className="absolute z-20 top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-xl shadow-xl divide-y divide-slate-100 max-h-56 overflow-y-auto custom-scrollbar animate-in fade-in duration-100"
              >
                <div className="p-1.5 bg-slate-50 text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                  Sugestões encontradas no catálogo:
                </div>
                {suggestions.map((prod) => (
                  <button
                    key={prod.sku || prod.id}
                    type="button"
                    onClick={() => handleSelectProduct(prod)}
                    className="w-full p-2.5 text-left hover:bg-amber-50/80 flex items-center gap-2.5 transition cursor-pointer"
                  >
                    <div className="w-8 h-8 rounded bg-slate-100 border border-slate-200 flex items-center justify-center shrink-0 overflow-hidden">
                      {prod.image || prod.arquivos_imagens?.[0] ? (
                        <img 
                          src={prod.image || prod.arquivos_imagens?.[0]} 
                          alt="" 
                          className="w-full h-full object-cover" 
                        />
                      ) : (
                        <Disc size={14} className="text-slate-400" />
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-mono font-black text-xs text-slate-900">{prod.skuPai || prod.sku}</span>
                        {prod.category && (
                          <span className="text-[10px] bg-slate-100 text-slate-600 px-1.5 py-0.2 rounded font-medium border border-slate-200">
                            {prod.category}
                          </span>
                        )}
                      </div>
                      <p className="text-[11px] text-slate-500 truncate">
                        {prod.name || prod.full_data?.name || "Sem descrição"}
                      </p>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* ALERTA SE O SKU JÁ POSSUI MOLDE CADASTRADO */}
          {cleanPai && existingMolde && (
            <div className="bg-amber-50 border border-amber-300 rounded-xl p-3.5 space-y-2 animate-in fade-in duration-150">
              <div className="flex items-start gap-2.5">
                <AlertCircle size={18} className="text-amber-700 shrink-0 mt-0.5" />
                <div className="text-xs text-amber-950">
                  <p className="font-black">Este SKU já possui molde de borracha cadastrado na fábrica!</p>
                  <p className="text-[11px] text-amber-800 mt-0.5">
                    O molde <strong>{cleanPai}</strong> já está ativo na Central de Subestoque. Você pode abrir diretamente as configurações técnicas dele.
                  </p>
                </div>
              </div>

              <div className="pt-1 flex items-center justify-end">
                <button
                  type="button"
                  onClick={() => {
                    if (onSuccess) {
                      onSuccess({ skuPai: cleanPai, openConfigImmediately: true });
                    }
                    onClose();
                  }}
                  className="px-3 py-1.5 bg-amber-600 hover:bg-amber-700 text-white rounded-lg text-xs font-bold transition flex items-center gap-1.5 shadow-2xs cursor-pointer"
                >
                  <Settings2 size={13} />
                  <span>Abrir Configuração da Borracha</span>
                </button>
              </div>
            </div>
          )}

          {/* DADOS DO PRODUTO DETECTADO */}
          {cleanPai && selectedProduct && !existingMolde && (
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 flex items-center gap-3 animate-in fade-in duration-100">
              <div className="w-10 h-10 rounded-lg bg-white border border-slate-200 flex items-center justify-center shrink-0 overflow-hidden shadow-2xs">
                {selectedProduct.image || selectedProduct.arquivos_imagens?.[0] ? (
                  <img 
                    src={selectedProduct.image || selectedProduct.arquivos_imagens?.[0]} 
                    alt="" 
                    className="w-full h-full object-cover" 
                  />
                ) : (
                  <Disc size={18} className="text-amber-600" />
                )}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-mono font-black text-xs text-slate-900">{cleanPai}</span>
                  <span className="text-[10px] bg-emerald-100 text-emerald-800 px-1.5 py-0.2 rounded font-bold border border-emerald-300 flex items-center gap-0.5">
                    <Check size={10} />
                    Catálogo Vinculado
                  </span>
                </div>
                <p className="text-xs text-slate-600 font-medium truncate mt-0.5">
                  {selectedProduct.name || selectedProduct.full_data?.name || "Produto cadastrado"}
                </p>
              </div>
            </div>
          )}

          {/* CAMPOS DE INICIALIZAÇÃO DO NOVO MOLDE */}
          {cleanPai && !existingMolde && (
            <div className="space-y-3 pt-1 border-t border-slate-100 animate-in fade-in duration-150">
              {/* TIPO / MODELO DE JOIA */}
              <div className="space-y-1">
                <label className="text-[11px] font-bold text-slate-700 uppercase flex items-center gap-1">
                  <Sparkles size={12} className="text-amber-600" />
                  <span>Tipo / Modelo de Joia</span>
                </label>
                <select
                  value={tipoModelo}
                  onChange={(e) => setTipoModelo(e.target.value)}
                  className="w-full px-3 py-1.5 bg-slate-50 border border-slate-300 rounded-lg text-xs font-bold text-slate-800 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:bg-white transition cursor-pointer"
                >
                  {MODELOS_CANONICOS.filter(m => m !== "Todos").map(m => (
                    <option key={m} value={m}>{m}</option>
                  ))}
                </select>
              </div>

              {/* LOCALIZAÇÃO / GAVETA FÍSICA */}
              <div className="space-y-1">
                <label className="text-[11px] font-bold text-slate-700 uppercase flex items-center gap-1">
                  <MapPin size={12} className="text-slate-500" />
                  <span>Posição / Gaveta Física na Fábrica (Opcional)</span>
                </label>
                <input
                  type="text"
                  placeholder="Ex: Gaveta G-04, Caixa Bicos, Prateleira 2..."
                  value={gaveta}
                  onChange={(e) => setGaveta(e.target.value.toUpperCase())}
                  className="w-full px-3 py-1.5 bg-slate-50 border border-slate-300 rounded-lg text-xs font-semibold uppercase text-slate-800 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:bg-white transition"
                />
              </div>

              {/* SALDO INICIAL DE CERAS DISPONÍVEIS NA FÁBRICA */}
              <div className="space-y-1.5 pt-1">
                <label className="text-[11px] font-bold text-slate-700 uppercase flex items-center gap-1">
                  <Boxes size={13} className="text-indigo-600" />
                  <span>Saldo Inicial de Ceras Prontas na Gaveta (Opcional)</span>
                </label>

                <div className="grid grid-cols-2 gap-3">
                  {/* Cera Bruta */}
                  <div className="bg-slate-50 p-2.5 rounded-xl border border-slate-200 space-y-1">
                    <span className="text-[10px] font-bold text-slate-600 uppercase flex items-center gap-1">
                      <Flame size={11} className="text-amber-600" />
                      <span>Cera Bruta</span>
                    </span>
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => setCeraBruta(prev => String(Math.max(0, (parseInt(prev, 10) || 0) - 1)))}
                        className="w-7 h-7 rounded bg-white hover:bg-slate-200 border border-slate-300 font-black text-xs flex items-center justify-center cursor-pointer active:scale-95"
                      >
                        <Minus size={11} />
                      </button>
                      <input
                        type="text"
                        inputMode="numeric"
                        value={ceraBruta}
                        onChange={(e) => setCeraBruta(e.target.value.replace(/\D/g, ""))}
                        onBlur={() => {
                          if (!ceraBruta || isNaN(parseInt(ceraBruta, 10))) setCeraBruta("0");
                        }}
                        className="flex-1 min-w-0 h-7 text-center font-bold text-xs bg-white border border-slate-300 rounded focus:outline-none focus:ring-1 focus:ring-amber-500"
                      />
                      <button
                        type="button"
                        onClick={() => setCeraBruta(prev => String((parseInt(prev, 10) || 0) + 1))}
                        className="w-7 h-7 rounded bg-white hover:bg-slate-200 border border-slate-300 font-black text-xs flex items-center justify-center cursor-pointer active:scale-95"
                      >
                        <Plus size={11} />
                      </button>
                    </div>
                  </div>

                  {/* Cera Pré-Cravada */}
                  <div className="bg-slate-50 p-2.5 rounded-xl border border-slate-200 space-y-1">
                    <span className="text-[10px] font-bold text-slate-600 uppercase flex items-center gap-1">
                      <Gem size={11} className="text-purple-600" />
                      <span>Pré-Cravada</span>
                    </span>
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => setCeraPrecravada(prev => String(Math.max(0, (parseInt(prev, 10) || 0) - 1)))}
                        className="w-7 h-7 rounded bg-white hover:bg-slate-200 border border-slate-300 font-black text-xs flex items-center justify-center cursor-pointer active:scale-95"
                      >
                        <Minus size={11} />
                      </button>
                      <input
                        type="text"
                        inputMode="numeric"
                        value={ceraPrecravada}
                        onChange={(e) => setCeraPrecravada(e.target.value.replace(/\D/g, ""))}
                        onBlur={() => {
                          if (!ceraPrecravada || isNaN(parseInt(ceraPrecravada, 10))) setCeraPrecravada("0");
                        }}
                        className="flex-1 min-w-0 h-7 text-center font-bold text-xs bg-white border border-slate-300 rounded focus:outline-none focus:ring-1 focus:ring-purple-500"
                      />
                      <button
                        type="button"
                        onClick={() => setCeraPrecravada(prev => String((parseInt(prev, 10) || 0) + 1))}
                        className="w-7 h-7 rounded bg-white hover:bg-slate-200 border border-slate-300 font-black text-xs flex items-center justify-center cursor-pointer active:scale-95"
                      >
                        <Plus size={11} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* RODAPÉ DE AÇÕES */}
        <div className="p-4 bg-slate-50 border-t border-slate-100 flex items-center justify-between gap-2 shrink-0 flex-wrap">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="px-4 py-2 bg-white hover:bg-slate-100 text-slate-700 rounded-xl text-xs font-bold border border-slate-200 transition cursor-pointer"
          >
            Cancelar
          </button>

          {!existingMolde && cleanPai && (
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => handleSave(false)}
                disabled={saving}
                className="px-4 py-2 bg-slate-800 hover:bg-slate-900 text-white rounded-xl text-xs font-bold transition flex items-center gap-1.5 cursor-pointer disabled:opacity-50"
              >
                {saving ? (
                  <RefreshCw size={13} className="animate-spin" />
                ) : (
                  <Check size={13} />
                )}
                <span>Salvar Molde</span>
              </button>

              <button
                type="button"
                onClick={() => handleSave(true)}
                disabled={saving}
                className="px-4 py-2 bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-700 hover:to-amber-800 text-white rounded-xl text-xs font-black shadow-sm transition flex items-center gap-1.5 cursor-pointer disabled:opacity-50 active:scale-95"
              >
                {saving ? (
                  <RefreshCw size={13} className="animate-spin" />
                ) : (
                  <Settings2 size={13} />
                )}
                <span>Salvar e Configurar Borracha</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
